import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserCircle, Fingerprint, Camera, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import OperatoreSchedaView from "../components/OperatoreSchedaView";
import { NoteList } from "../components/NoteList";
import { DatiBancari } from "../components/DatiBancari";
import BustePagaRO from "../components/BustePagaRO";
import MioDocumenti from "../components/MioDocumenti";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const MIEI_CAMPI = [
  { key: "data_nascita", label: "Data di nascita", type: "date" },
  { key: "luogo_nascita", label: "Luogo di nascita", type: "text" },
  { key: "codice_fiscale", label: "Codice fiscale", type: "text" },
  { key: "n_documento", label: "N° carta d'identità", type: "text" },
  { key: "telefono", label: "Telefono", type: "text" },
  { key: "email", label: "Email", type: "email" },
];

const ModificaMieiDati = ({ op, opId, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const start = () => {
    const f = {}; MIEI_CAMPI.forEach((c) => { f[c.key] = op[c.key] || ""; });
    setForm(f); setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/firma/mio-account/dati-personali?op_id=${opId}`, form);
      toast.success("Dati aggiornati");
      onSaved && onSaved();
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={start} data-testid="mio-edit-dati"><Pencil size={14} className="mr-1.5" /> Modifica i miei dati</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mio-edit-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Modifica i miei dati</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Puoi modificare solo questi dati personali. Per altre variazioni contatta l'amministrazione.</p>
          <div className="grid gap-3 mt-1">
            {MIEI_CAMPI.map((c) => (
              <div key={c.key}>
                <Label className="text-xs font-semibold mb-1 block">{c.label}</Label>
                <Input type={c.type} value={form[c.key] || ""} onChange={(e) => setForm((p) => ({ ...p, [c.key]: e.target.value }))} data-testid={`mio-edit-${c.key}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="mio-edit-save">{saving ? "Salvo…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Foto = ({ op, onChanged }) => {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  useEffect(() => {
    let obj = null;
    if (op?.foto_path) {
      api.get(`/firma/mio-account/foto`, { params: { op_id: op.id }, responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => setUrl(null));
    } else setUrl(null);
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [op?.id, op?.foto_path]);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); e.target.value = ""; return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/firma/mio-account/foto?op_id=${op.id}`, fd); toast.success("Foto aggiornata"); onChanged && onChanged(); }
    catch { toast.error("Errore caricamento foto"); } finally { setBusy(false); e.target.value = ""; }
  };
  const removePhoto = async () => {
    setBusy(true);
    try { await api.delete(`/firma/mio-account/foto`, { params: { op_id: op.id } }); setUrl(null); toast.success("Foto rimossa"); onChanged && onChanged(); }
    catch { toast.error("Errore rimozione foto"); } finally { setBusy(false); }
  };
  const initials = `${op?.nome?.[0] || ""}${op?.cognome?.[0] || ""}`;
  return (
    <div className="relative h-20 w-20 shrink-0" data-testid="mio-foto">
      {url
        ? <img src={url} alt={initials} className="h-20 w-20 rounded-2xl object-cover border" data-testid="mio-foto-img" />
        : <div className="h-20 w-20 rounded-2xl grid place-items-center text-white text-2xl font-bold" style={{ background: "var(--gr-blue)" }}>{initials}</div>}
      <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="mio-foto-upload" title="Carica / cambia foto"
        className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-white border shadow grid place-items-center hover:bg-secondary transition-colors disabled:opacity-50"><Camera size={14} /></button>
      {url && <button onClick={removePhoto} disabled={busy} data-testid="mio-foto-remove" title="Rimuovi foto"
        className="absolute -bottom-1.5 -left-1.5 h-7 w-7 rounded-full bg-white border shadow grid place-items-center hover:bg-destructive/10 transition-colors disabled:opacity-50"><Trash2 size={13} className="text-destructive" /></button>}
      <input type="file" accept="image/*" ref={fileRef} onChange={onFile} className="hidden" data-testid="mio-foto-input" />
    </div>
  );
};

export default function MioAccount() {
  const { structures = [], user } = useAuth();
  const { identity } = useFirma();
  const qc = useQueryClient();
  const opId = identity?.operatore_id;

  const { data: o, isError, isLoading } = useQuery({
    queryKey: ["mio-account", opId],
    enabled: !!opId, retry: false,
    queryFn: async () => (await api.get(`/firma/mio-account`, { params: { op_id: opId } })).data,
  });
  const reloadFoto = () => qc.invalidateQueries({ queryKey: ["mio-account", opId] });

  const noOperatore = !opId || isError || (!isLoading && !o);
  if (noOperatore) {
    return (
      <div data-testid="mio-account-page">
        <div className="flex items-center gap-2 mb-4"><UserCircle size={22} className="text-primary" /><h1 className="text-2xl font-head font-bold">Il mio account</h1></div>
        <div className="gr-card p-8 text-center" data-testid="mio-account-nessuno">
          <Fingerprint size={36} className="mx-auto mb-3 text-primary opacity-60" />
          <p className="text-sm text-muted-foreground max-w-md mx-auto">Nessuna scheda operatore collegata a questo accesso.<br />Effettua la <b>Firma Rapida</b> con il tuo PIN per visualizzare la tua scheda personale.</p>
        </div>
      </div>
    );
  }
  if (isLoading || !o) return <div className="text-muted-foreground">Caricamento...</div>;

  return (
    <div data-testid="mio-account-page">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2"><UserCircle size={22} className="text-primary" /><h1 className="text-2xl font-head font-bold">Il mio account</h1></div>
        <ModificaMieiDati op={o} opId={opId} onSaved={() => qc.invalidateQueries({ queryKey: ["mio-account", opId] })} />
      </div>
      <OperatoreSchedaView op={o} opId={opId} structures={structures} manage={false} fallbackName={user?.name} foto={<Foto op={o} onChanged={reloadFoto} />}
        documentiNode={<MioDocumenti opId={opId} title="Documenti" testidSuffix="doc" defaultCategoria="Documento identità"
          categories={["Contratto di lavoro completo", "Documento identità", "Patente", "Tessera sanitaria", "Codice fiscale", "Curriculum", "Idoneità sanitaria", "Altro"]}
          obbligatori={(o?.contratto || "").toLowerCase().includes("cooperativ") ? [] : [["Contratto di lavoro completo", ["Contratto di lavoro"], "Il contratto firmato, tutte le pagine."]]}
          docFilter={(d) => d.categoria !== "Attestato"} />}
        attestatiNode={<MioDocumenti opId={opId} title="Attestati" testidSuffix="att" defaultCategoria="Attestato"
          categories={["Attestato"]} docFilter={(d) => d.categoria === "Attestato"} />}
        extraTabs={[{ value: "note", label: "Note", testid: "mytab-note", node: <NoteList /> }, { value: "dati-bancari", label: "Dati bancari", testid: "mytab-dati-bancari", node: <DatiBancari op={o} opId={opId} /> }, { value: "buste", label: "Buste paga", testid: "mytab-buste", node: <BustePagaRO opId={opId} dataAssunzione={(o?.contratto || "").toLowerCase().includes("cooperativ") ? "" : o?.data_assunzione} /> }]} />
    </div>
  );
}
